import {Tooltip} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {CloseFullscreen, OpenInFull} from "@mui/icons-material";
import Box from "@mui/material/Box";
import {useRef, useState, useEffect} from "react";
import {Niivue, SLICE_TYPE} from "@niivue/niivue";
import CircularProgress from "@mui/material/CircularProgress";

// LUT for marker volume: index 1 = green (positive click), index 2 = red (negative click)
const MARKER_LUT = {
    R: [0, 0, 255],
    G: [0, 255, 0],
    B: [0, 0, 0],
    A: [0, 255, 255],
    I: [0, 1, 2],
    labels: ['', 'positive', 'negative']
};

export default function MRIViewport({ slice, imageVolId, segVolId,
                                      maximized, setMaximized, imageVolRef, segVolRef,
                                      imageLoading=undefined, segLoading=undefined, 
                                      isDownloading, isCorrectionLoading=false, onUpdateComplete=undefined,
                                      hidden=false, isCorrecting=false,
                                      setClickCoords=undefined, clickInformation=undefined,
                                      clickHistory=undefined, markerRedrawSignal=0
                                        }) {
    const canvasRef = useRef();
    const viewportRef = useRef(null);
    const resizeFrameRef = useRef(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipText, setTooltipText] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const labelTemplateRef = useRef(null);
    const isDraggingRef = useRef(false);

    useEffect(() => {
        let resizeObserver = null;
        const initialize = async () => {
            const viewOptions = {
                crosshairWidth: 0, multiplanarShowRender: 0, sagittalNoseLeft: true,
                atlasOutline: 1, isRadiologicalConvention: true, showBoundsBorder: false, multiplanarLayout: 0,
                isOrientationTextVisible: false, dragMode: 4
            }
            const viewport = new Niivue(viewOptions);
            viewport.canvasPos2frac()
            viewport.opts.sliceType = slice;
            viewportRef.current = viewport;
            await viewportRef.current.attachToCanvas(canvasRef.current);
            fetch('shared-assets/label-legends/label_legend.json').then(response => response.json().then(data => {
                labelTemplateRef.current = Object.fromEntries(Object.entries(data).map(a => a.reverse()))
            }));
            addViewportHover(setTooltipOpen);

            // Add ResizeObserver to handle responsive canvas sizing
            const canvas = canvasRef.current;
            const parent = canvas?.parentNode;
            if (canvas && parent && typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(() => {
                    if (parent && viewportRef.current) {
                        const w = parent.clientWidth;
                        const h = parent.clientHeight;
                        if (w > 0 && h > 0) {
                            canvas.width = w;
                            canvas.height = h;
                            viewportRef.current.resizeListener();
                        }
                    }
                });
                resizeObserver.observe(parent);
            }
        }
        initialize();
        return () => {
            if (resizeFrameRef.current !== null) {
                cancelAnimationFrame(resizeFrameRef.current);
            }
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, []);

    const removeAllVolumes = () => {
        const numVolumes = viewportRef.current.volumes.length;
        for (let i = 0; i < numVolumes; i++) {
            viewportRef.current.removeVolumeByIndex(0);
        }
    }

    const removeSegVolume = () => {
        if (viewportRef.current.volumes.length > 1) viewportRef.current.removeVolumeByIndex(1);
    }

    const removeMarkerVolume = () => {
        if (viewportRef.current.volumes.length > 2) viewportRef.current.removeVolumeByIndex(2);
    }

    const addMarkerVolume = () => {
        if (!viewportRef.current) return false;
        if (viewportRef.current.opts.sliceType === SLICE_TYPE.RENDER) {
            return false;
        }
        if (viewportRef.current.volumes.length === 2) {
            const markerVol = viewportRef.current.volumes[1].clone();
            markerVol.zeroImage();
            markerVol.setColormapLabel(MARKER_LUT);
            markerVol.opacity = 1.0;
            viewportRef.current.addVolume(markerVol);
            viewportRef.current.setVolume(markerVol, 2);
            return true;
        }
        return viewportRef.current.volumes.length > 2;
    }

    useEffect(() => {
        if (!viewportRef.current) return;
        if (isCorrecting) {
            addMarkerVolume();
        } else {
            removeMarkerVolume();
        }
        viewportRef.current.updateGLVolume();
    }, [isCorrecting, imageVolId, segVolId]);

    useEffect(() => {
        if (!imageVolRef.current) return;
        setIsUpdating(true);
        removeAllVolumes();
        viewportRef.current.addVolume(imageVolRef.current);
        viewportRef.current.setVolume(imageVolRef.current, 0);
        if (isCorrecting) {
            addMarkerVolume();
        }
        viewportRef.current.updateGLVolume();
        setIsUpdating(false);
    }, [imageVolId, isCorrecting]);

    useEffect(() => {
        if (!segVolRef.current) return;
        setIsUpdating(true)
        removeSegVolume();
        viewportRef.current.addVolume(segVolRef.current);
        viewportRef.current.setVolume(segVolRef.current, 1);
        if (isCorrecting) {
            addMarkerVolume();
        }
        viewportRef.current.updateGLVolume();
        setIsUpdating(false);
        if (typeof onUpdateComplete === 'function') {
            onUpdateComplete();
        }
    }, [segVolId, isCorrecting]);

    useEffect(() => {
        if (!clickInformation?.position) return;
        if (!viewportRef.current || viewportRef.current.volumes.length <= 2 || !viewportRef.current.volumes[2]) return;
        const dims = viewportRef.current.volumes[2].dimsRAS; // [nDims, dimX, dimY, dimZ, ...]
        
        const value = clickInformation.value;
        addCorrectionPoint(clickInformation.position, dims, value);
        
        viewportRef.current.updateGLVolume();
    }, [clickInformation, imageVolId, isCorrecting]);

    // Undo: rebuild the marker volume from scratch from the (already-trimmed)
    // click history, rather than surgically erasing a point - correction
    // radii can overlap, so redrawing the remaining history is the only way
    // to guarantee undone markers are fully cleared.
    useEffect(() => {
        if (markerRedrawSignal === 0) return;
        if (!viewportRef.current || viewportRef.current.volumes.length <= 2 || !viewportRef.current.volumes[2]) return;
        viewportRef.current.volumes[2].zeroImage();
        const dims = viewportRef.current.volumes[2].dimsRAS;
        (clickHistory.current || []).forEach(({ position, value }) => {
            addCorrectionPoint(position, dims, value);
        });
    }, [markerRedrawSignal]);

    useEffect(() => {
        const loading = (typeof imageLoading === 'boolean' || typeof segLoading === 'boolean')
            ? !!(imageLoading || segLoading)
            : !!isDownloading;
        setIsUpdating(loading);
    }, [imageLoading, segLoading, isDownloading]);

    useEffect(() => {
        if (!viewportRef.current) return;
        const canvas = viewportRef.current.canvas;
        const parentDiv = canvas?.parentNode;
        const viewportContainer = parentDiv?.parentNode;

        if (!canvas || !parentDiv || !viewportContainer) return;

        const resizeCanvas = (width, height) => {
            if (resizeFrameRef.current !== null) {
                cancelAnimationFrame(resizeFrameRef.current);
            }

            resizeFrameRef.current = requestAnimationFrame(() => {
                if (viewportRef.current) {
                    viewportRef.current.canvas.width = width;
                    viewportRef.current.canvas.height = height;
                    viewportRef.current.resizeListener();
                }
                resizeFrameRef.current = null;
            });
        };

        if (maximized === slice) {
            // Calculate size - use smaller of 1000px or available viewport space (accounting for sidebar ~250px)
            const availableWidth = window.innerWidth - 300; // Sidebar + padding
            const availableHeight = window.innerHeight - 100; // Breadcrumb + padding
            const size = Math.max(200, Math.min(1000, availableWidth, availableHeight));
            Object.assign(parentDiv.style, {width: `${size}px`, height: `${size}px`, margin: '0 auto'});
            resizeCanvas(size, size);
        } else if (maximized === -1) {
            Object.assign(parentDiv.style, {width: '100%', height: '100%', margin: ''});
            viewportContainer.hidden = hidden;
            if (parentDiv.clientWidth > 0 && parentDiv.clientHeight > 0) {
                resizeCanvas(parentDiv.clientWidth, parentDiv.clientHeight);
            }
        } else {
            viewportContainer.hidden = true;
        }

        return () => {
            if (resizeFrameRef.current !== null) {
                cancelAnimationFrame(resizeFrameRef.current);
                resizeFrameRef.current = null;
            }
        };
    }, [hidden, maximized, slice]);

    function addViewportHover(openFunction) {
        viewportRef.current.canvas.addEventListener('mousemove', (event) => {
            getSegmentationValue(event, openFunction);
            if (event.buttons === 1)
                isDraggingRef.current = true;
        })
        viewportRef.current.canvas.addEventListener('mouseout', () => {
            openFunction(false);
        })
        viewportRef.current.canvas.addEventListener('wheel', (event) => {
            getSegmentationValue(event, openFunction);
        })
        viewportRef.current.canvas.addEventListener('mouseup', (event) => {
            if (event.button === 0) {
                if (!isDraggingRef.current && setClickCoords) {
                    const voxCoords = getVoxelCoords(event);
                    if (voxCoords)
                        setClickCoords(voxCoords);
                }
                isDraggingRef.current = false;
            }
        })
    }

    const getVoxelCoords = (event) => {
        const canvas = event.target;
        const canvasX = event.offsetX * canvas.width / canvas.offsetWidth;
        const canvasY = event.offsetY * canvas.height / canvas.offsetHeight;
        const canvasPos = [canvasX, canvasY];
        const fracPos = viewportRef.current.canvasPos2frac(canvasPos);
        const inVol = fracPos.every((e) => e !== -1);
        if (!inVol)
            return false
        return viewportRef.current.frac2vox(fracPos);
    }

    const getSegmentationValue = (event, openFunction) => {
        const segVol = segVolRef?.current;
        if (!segVol) {
            if (viewportRef.current.volumes.length <= 1) return;
            const voxPos = getVoxelCoords(event);
            const segValue = viewportRef.current.volumes[1].getValue(voxPos[0], voxPos[1], voxPos[2]);
            if (segValue > 0) {
                setTooltipText(labelTemplateRef.current[segValue]);
                openFunction(true);
            } else {
                openFunction(false);
            }
            return;
        }

        const voxPos = getVoxelCoords(event);
        const segValue = segVol.getValue(voxPos[0], voxPos[1], voxPos[2]);
        if (segValue > 0) {
            setTooltipText(labelTemplateRef.current[segValue]);
            openFunction(true);
        } else {
            openFunction(false);
        }
    }

    const addCorrectionPoint = (clickPos, dims, value) => {
        const [cx, cy, cz] = clickPos;
        const dimX = dims[1];
        const dimY = dims[2];
        const dimZ = dims[3];
        const radius = 2; // voxel radius — smaller radius keeps it close to the click point

        const outerSq = radius * radius;

        for (let dx = -radius; dx <= radius; dx++) {
            for (let dy = -radius; dy <= radius; dy++) {
                for (let dz = -radius; dz <= radius; dz++) {
                    const distSq = dx * dx + dy * dy + dz * dz;
                    if (distSq <= outerSq) {
                        const x = cx + dx;
                        const y = cy + dy;
                        const z = cz + dz;
                        if (x >= 0 && x < dimX && y >= 0 && y < dimY && z >= 0 && z < dimZ) {
                            viewportRef.current.volumes[2].img[x + y * dimX + z * dimX * dimY] = value;
                        }
                    }
                }
            }
        }
    }

    return (
        <Box component="section" sx={{px: 0.05, py: 0, width: '100%', height: '100%'}} hidden={hidden}>
            <Tooltip title={tooltipText} open={tooltipOpen} followCursor>
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        position: 'relative'
                    }}
                >
                        <div style={{
                        display: (isUpdating || !imageVolRef.current) ? 'flex' : 'none',
                        position: 'absolute',
                        inset: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isCorrectionLoading ? 'transparent' : '#000',
                        zIndex: 3,
                        pointerEvents: isCorrectionLoading ? 'none' : 'auto'
                    }}>
                        <CircularProgress disableShrink />
                    </div>

                    <canvas id={slice.toString()} ref={canvasRef}
                            style={{border: '0px', position: 'absolute', width: '100%', height: '100%'}}/>
                        <IconButton sx={{
                            position: 'absolute', top: '10px', right: '10px', color: "rgba(255, 255, 255, 0.9)",
                            backgroundColor: "rgba(150, 150, 150, 0.5)", 'z-index': 1, '&:hover': {
                                backgroundColor: "rgba(150, 150, 150, 0.5)"
                            }
                        }} onClick={() => maximized === slice ? setMaximized(-1) : setMaximized(slice)}>
                            {maximized === slice ? <CloseFullscreen/> : <OpenInFull/>}
                        </IconButton>
                </div>

            </Tooltip>
        </Box>
    )
}